import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["date", "scheduleWrapper", "schedule", "timeslotWrapper", "timeslot", "partySizeWrapper", "partySize", "ticketWrapper", "ticketError", "mobile", "tipField", "reselectButton"]
  static values = {
    originalSchedule: String,
    originalTimeslot: String,
    hasOrder: Boolean
  }

  connect() {
    const checked = this.element.querySelector('input[name="payment_status"]:checked')
    if (checked) this.paymentStatusChanged({ target: checked })
  }

  paymentStatusChanged(event) {
    if (!this.hasTipFieldTarget) return
    const showTip = event.target.value === "send_invoice"
    this.tipFieldTarget.classList.toggle("d-none", !showTip)
    if (!showTip) {
      const input = this.tipFieldTarget.querySelector("input")
      if (input) input.value = ""
    }
  }

  dateChanged(event) {
    const date = event.target.value
    if (!date) return

    this.savedTicketSelections = this.captureTicketSelections()
    this.clearSchedule()
    this.clearTimeslot()
    this.clearTickets()

    fetch(`/companies/schedule_info/schedules_for_date?date=${date}&stimulus_controller=edit-reservation`, {
      headers: { "Accept": "text/html" }
    })
      .then(response => response.text())
      .then(html => {
        this.scheduleWrapperTarget.innerHTML = html
        this.restoreOriginalSchedule()
      })
  }

  restoreOriginalSchedule() {
    if (!this.originalScheduleValue) return

    const select = this.scheduleWrapperTarget.querySelector('select')
    if (!select) return

    const option = select.querySelector(`option[value="${this.originalScheduleValue}"]`)
    if (option) {
      select.value = this.originalScheduleValue
      this.scheduleChanged({ target: select })
    }
  }

  scheduleChanged(event) {
    const scheduleId = event.target.value
    const date = this.dateTarget.value
    if (!scheduleId || !date) return

    this.clearTimeslot()
    this.updateMobileRequired(event.target)

    fetch(`/companies/schedule_info/timeslots_for_schedule?date=${date}&schedule_id=${scheduleId}&stimulus_controller=edit-reservation`, {
      headers: { "Accept": "text/html" }
    })
      .then(response => response.text())
      .then(html => {
        this.timeslotWrapperTarget.innerHTML = html
        this.restoreOriginalTimeslot()
      })

    if (this.hasTicketWrapperTarget) {
      const current = this.captureTicketSelections()
      if (Object.keys(current).length > 0) this.savedTicketSelections = current
      this.fetchTickets(scheduleId)
    }
  }

  fetchTickets(scheduleId) {
    fetch(`/companies/schedule_info/tickets_for_schedule?schedule_id=${scheduleId}`, {
      headers: { "Accept": "text/html" }
    })
      .then(response => response.text())
      .then(html => {
        if (this.hasTicketWrapperTarget) {
          this.ticketWrapperTarget.innerHTML = html
          this.restoreTicketSelections()
        }
      })
  }

  // Ticket quantities are keyed by input name ("tickets[<id>]"), so a restore
  // only applies when the refetched list is for the same schedule — a different
  // tour type has different ticket ids and correctly starts at zero.
  captureTicketSelections() {
    if (!this.hasTicketWrapperTarget) return {}

    const selections = {}
    this.ticketWrapperTarget.querySelectorAll('input[name^="tickets["]').forEach(input => {
      selections[input.name] = input.value
    })
    return selections
  }

  restoreTicketSelections() {
    const saved = this.savedTicketSelections
    this.savedTicketSelections = null
    if (!saved) return

    Object.entries(saved).forEach(([name, value]) => {
      const input = this.ticketWrapperTarget.querySelector(`input[name="${name}"]`)
      if (input) input.value = value
    })
  }

  // Referenced by the timeslot select's change action; changing the time has
  // no side effects on the edit form, but the handler must exist for Stimulus.
  timeslotChanged() {}

  // Opt-in exit from the mismatched-tickets state: load the current
  // schedule's editable ticket list (all zeros — the old quantities are
  // keyed to another schedule's tickets and can't carry over) below the
  // read-only purchase reference, which stays visible. Saving then goes
  // through the normal void-and-replace flow.
  reselectTickets() {
    if (!this.hasScheduleTarget || !this.scheduleTarget.value) return

    this.fetchTickets(this.scheduleTarget.value)
    if (this.hasReselectButtonTarget) this.reselectButtonTarget.classList.add("d-none")
  }

  restoreOriginalTimeslot() {
    if (!this.originalTimeslotValue) return

    const select = this.timeslotWrapperTarget.querySelector('select')
    if (!select) return

    const option = select.querySelector(`option[value="${this.originalTimeslotValue}"]`)
    if (option) {
      select.value = this.originalTimeslotValue
    }
  }

  updateMobileRequired(select) {
    const selectedOption = select.options[select.selectedIndex]
    const requiresPhone = selectedOption?.dataset?.requiresPhone === "true"
    this.mobileTarget.required = requiresPhone
  }

  clearSchedule() {
    this.scheduleWrapperTarget.innerHTML = `
      <select name="reservation[schedule_id]" class="form-select" required disabled>
        <option value="">Select tour type...</option>
      </select>
    `
  }

  clearTimeslot() {
    this.timeslotWrapperTarget.innerHTML = `
      <select name="reservation[timeslot]" class="form-select" required disabled>
        <option value="">Select time...</option>
      </select>
    `
  }

  validateTickets(event) {
    if (!this.hasTicketWrapperTarget) return

    const ticketInputs = this.ticketWrapperTarget.querySelectorAll('input[name^="tickets["]')
    if (ticketInputs.length === 0) return

    const totalTickets = Array.from(ticketInputs).reduce((sum, input) => sum + parseInt(input.value || 0, 10), 0)
    const partySize = parseInt(this.partySizeTarget.value || 0, 10)

    // All zeros is only valid when the reservation has no order to remove
    // (legacy ticketless reservations); otherwise tickets must match the
    // party size, which is always at least 1.
    if (totalTickets === 0 && !this.hasOrderValue) {
      this.ticketErrorTarget.classList.add("d-none")
      return
    }
    if (totalTickets !== partySize) {
      event.preventDefault()
      this.ticketErrorTarget.textContent = `Total tickets (${totalTickets}) must match the party size (${partySize}).`
      this.ticketErrorTarget.classList.remove("d-none")
    } else {
      this.ticketErrorTarget.classList.add("d-none")
    }
  }

  clearTickets() {
    if (this.hasTicketWrapperTarget) {
      this.ticketWrapperTarget.innerHTML = `
        <div class="text-muted small">Select a tour type to see available tickets.</div>
      `
    }
  }
}
